package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.MyTipShare
import com.evoapps.evoshift.domain.model.TipPeriod
import com.evoapps.evoshift.domain.model.TipPool
import com.evoapps.evoshift.domain.model.TipPoolDraft
import com.evoapps.evoshift.domain.model.TipSplit
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.TipRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.tips.PreviewTipSplitUseCase
import com.evoapps.evoshift.domain.usecase.tips.SaveTipPoolUseCase
import com.evoapps.evoshift.domain.usecase.tips.TipPreview
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.reports.FileSaver
import com.evoapps.evoshift.ui.tips.TipExportLabels
import com.evoapps.evoshift.ui.tips.TipsExport
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.export_col_employee
import evoshift.composeapp.generated.resources.tips_csv_from
import evoshift.composeapp.generated.resources.tips_csv_hours
import evoshift.composeapp.generated.resources.tips_csv_role
import evoshift.composeapp.generated.resources.tips_csv_share
import evoshift.composeapp.generated.resources.tips_csv_to
import evoshift.composeapp.generated.resources.tips_deleted
import evoshift.composeapp.generated.resources.tips_exported
import evoshift.composeapp.generated.resources.tips_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import org.jetbrains.compose.resources.getString

/** The pool being entered or changed (Tips → New tip pool, or a pool's card). */
data class TipEditorState(
    val period: TipPeriod = TipPeriod.DAY,
    /** A day of the period; the period is the day or the workplace's week holding it. */
    val date: LocalDate,
    val amountText: String = "",
    /** Role → weight in percent as typed; empty or 100: the hour counts once. */
    val weights: Map<String, String> = emptyMap(),
    val preview: TipPreview? = null,
    /** The pool already stored for this period, if any (replaced when saved). */
    val existing: TipPool? = null,
    /** Saving was tried with an amount or weight that is not valid: show why. */
    val tried: Boolean = false
) {
    val amountMinor: Long? get() = TipSplit.parseAmount(amountText)

    /** Typed weights as numbers; null when one is not a whole percent 0..[TipSplit.MAX_WEIGHT]. */
    val parsedWeights: Map<String, Int>?
        get() = weights.mapValues { (_, text) -> text.trim().ifEmpty { "${TipSplit.DEFAULT_WEIGHT}" }.toIntOrNull()?.takeIf { it in 0..TipSplit.MAX_WEIGHT } }
            .takeIf { map -> map.values.none { it == null } }
            ?.mapValues { it.value!! }

    fun weightInvalid(role: String): Boolean =
        weights[role]?.trim()?.ifEmpty { "${TipSplit.DEFAULT_WEIGHT}" }?.toIntOrNull()?.let { it !in 0..TipSplit.MAX_WEIGHT } ?: false

    val canSave: Boolean get() = amountMinor != null && parsedWeights != null
}

data class TipsUiState(
    val isLoading: Boolean = true,
    val userId: String = "",
    val workplaceId: String? = null,
    val capabilities: Set<Capability> = emptySet(),
    /** The workplace's pools (managers). */
    val pools: List<TipPool> = emptyList(),
    /** The person's own shares. */
    val myShares: List<MyTipShare> = emptyList(),
    val editor: TipEditorState? = null,
    val isSaving: Boolean = false,
    /** A save, a removal, an export or a failure to tell about. */
    val message: UiText? = null
) {
    val seesPools: Boolean get() = capabilities.shows(Gate.TIP_POOLS)
    val canEdit: Boolean get() = capabilities.shows(Gate.TIP_POOLS_EDITING)
}

/**
 * SH-124: Tips (More → Tips). Everyone sees their own share of each pool ("Your share: 85 zł");
 * managers also see the workplace's pools, enter a day's or a week's pool with optional role
 * weights (previewed with [PreviewTipSplitUseCase], split again by the server on save), remove one
 * and export a pool's split as CSV. Saves are optimistic: the pool shows at once with the
 * preview's split and goes back if the server refuses.
 */
class TipsViewModel(
    private val tipRepository: TipRepository,
    private val previewTipSplit: PreviewTipSplitUseCase,
    private val saveTipPool: SaveTipPoolUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val fileSaver: FileSaver,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(TipsUiState())
    val uiState: StateFlow<TipsUiState> = _uiState.asStateFlow()
    private val previewJob = JobSlot()

    init {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                val user = getCurrentUser().first() ?: return@collectLatest
                if (workplaceId == null) {
                    _uiState.update { it.copy(isLoading = false, workplaceId = null, pools = emptyList(), myShares = emptyList()) }
                    return@collectLatest
                }
                val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
                _uiState.update { state ->
                    val switched = state.workplaceId != workplaceId
                    state.copy(
                        userId = user.uid, workplaceId = workplaceId, capabilities = capabilities,
                        isLoading = switched || state.isLoading,
                        pools = if (switched) emptyList() else state.pools,
                        myShares = if (switched) emptyList() else state.myShares,
                        editor = if (switched) null else state.editor
                    )
                }
                val pools = if (capabilities.shows(Gate.TIP_POOLS)) tipRepository.observePools(workplaceId) else flowOf(emptyList())
                val mine = if (capabilities.shows(Gate.MY_TIPS)) tipRepository.observeMyShares(user.uid, workplaceId) else flowOf(emptyList())
                combine(pools, mine) { p, m -> p to m }
                    .catch { e -> _uiState.update { it.copy(isLoading = false, message = e.toUiText()) } }
                    .collect { (p, m) -> _uiState.update { it.copy(isLoading = false, pools = p, myShares = m) } }
            }
        }
    }

    // --- The editor ---------------------------------------------------------------------------

    /** "New tip pool": today, one day. */
    fun openNew() {
        if (!_uiState.value.canEdit) return
        openEditor(TipEditorState(date = today()))
    }

    /** A pool's card: its period and amount, to change. */
    fun openPool(pool: TipPool) {
        if (!_uiState.value.canEdit) return
        openEditor(
            TipEditorState(
                period = pool.period,
                date = LocalDate.parse(pool.startDate),
                amountText = formatAmountInput(pool.amountMinor),
                weights = pool.roleWeights.mapValues { it.value.toString() },
                existing = pool
            )
        )
    }

    private fun openEditor(editor: TipEditorState) {
        _uiState.update { it.copy(editor = editor) }
        refreshPreview()
    }

    fun closeEditor() {
        previewJob.cancel()
        _uiState.update { it.copy(editor = null) }
    }

    fun setPeriod(period: TipPeriod) = updateEditor(preview = true) { it.copy(period = period) }

    /** The arrows beside the period: a day, or a week, back or forward. */
    fun movePeriod(steps: Int) = updateEditor(preview = true) { e ->
        e.copy(date = e.date.plus(DatePeriod(days = steps * if (e.period == TipPeriod.WEEK) TipSplit.WEEK_DAYS else 1)))
    }

    fun setAmount(text: String) = updateEditor(preview = true) { it.copy(amountText = text.take(16)) }

    fun setWeight(role: String, text: String) = updateEditor(preview = true) { e ->
        e.copy(weights = e.weights + (role to text.filter(Char::isDigit).take(3)))
    }

    private fun updateEditor(preview: Boolean, change: (TipEditorState) -> TipEditorState) {
        _uiState.update { s -> s.editor?.let { s.copy(editor = change(it)) } ?: s }
        if (preview) refreshPreview()
    }

    /** The split as it would be: the period's clocked hours, the amount and weights typed so far. */
    private fun refreshPreview() {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        val editor = state.editor ?: return
        previewJob.launch(viewModelScope) {
            previewTipSplit(workplaceId, editor.period, editor.date, editor.amountMinor ?: 0, editor.parsedWeights ?: emptyMap()).fold(
                onSuccess = { preview ->
                    _uiState.update { s ->
                        val e = s.editor ?: return@update s
                        val existing = s.pools.firstOrNull { it.id == TipSplit.poolId(preview.startDate, preview.endDate) }
                        // The roles of the period's hours, plus any weighted before.
                        val weights = (preview.roles.associateWith { "" } + e.weights)
                        s.copy(editor = e.copy(preview = preview, existing = existing, weights = weights))
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
            )
        }
    }

    /** Saves the pool; the server splits it. Optimistic: it shows at once with the preview's split. */
    fun save() {
        val before = _uiState.value
        val workplaceId = before.workplaceId ?: return
        val editor = before.editor ?: return
        if (!before.canEdit || before.isSaving) return
        val amount = editor.amountMinor
        val weights = editor.parsedWeights
        if (amount == null || weights == null) {
            _uiState.update { it.copy(editor = editor.copy(tried = true)) }
            return
        }
        val preview = editor.preview
        val start = preview?.startDate ?: editor.date.toString()
        val draft = TipPoolDraft(workplaceId, editor.period, start, amount, weights)
        val optimistic = preview?.let {
            TipPool(TipSplit.poolId(it.startDate, it.endDate), workplaceId, editor.period, it.startDate, it.endDate, amount, it.currency, weights, it.shares)
        }
        _uiState.update { s ->
            s.copy(
                isSaving = true,
                editor = null,
                pools = optimistic?.let { p -> (s.pools.filterNot { it.id == p.id } + p).sortedByDescending { it.startDate } } ?: s.pools
            )
        }
        viewModelScope.launch {
            saveTipPool(draft).fold(
                onSuccess = { saved ->
                    _uiState.update { s ->
                        s.copy(
                            isSaving = false,
                            pools = (s.pools.filterNot { it.id == saved.id } + saved).sortedByDescending { it.startDate },
                            message = UiText.Resource(Res.string.tips_saved)
                        )
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, pools = before.pools, editor = editor, message = e.toUiText()) } }
            )
        }
    }

    /** Removes [pool] and everyone's share of it. Optimistic. */
    fun delete(pool: TipPool) {
        val before = _uiState.value
        val workplaceId = before.workplaceId ?: return
        if (!before.canEdit) return
        _uiState.update { s -> s.copy(editor = null, pools = s.pools.filterNot { it.id == pool.id }) }
        viewModelScope.launch {
            tipRepository.deletePool(workplaceId, pool.id).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.tips_deleted)) } },
                onFailure = { e -> _uiState.update { it.copy(pools = before.pools, message = e.toUiText()) } }
            )
        }
    }

    /** [pool]'s split as a CSV, handed to the platform's [FileSaver]. */
    fun export(pool: TipPool) {
        val workplaceId = _uiState.value.workplaceId ?: return
        if (!_uiState.value.seesPools) return
        viewModelScope.launch {
            runCatching {
                val file = TipsExport.file(workplaceId, pool, exportLabels())
                fileSaver.save(file)
                file.name
            }.fold(
                onSuccess = { name -> _uiState.update { it.copy(message = UiText.Resource(Res.string.tips_exported, listOf(name))) } },
                onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }

    private suspend fun exportLabels() = TipExportLabels(
        language = appLanguage(),
        from = getString(Res.string.tips_csv_from),
        to = getString(Res.string.tips_csv_to),
        employee = getString(Res.string.export_col_employee),
        role = getString(Res.string.tips_csv_role),
        hours = getString(Res.string.tips_csv_hours),
        share = getString(Res.string.tips_csv_share)
    )

    companion object {
        /** grosze as the amount field shows them: "85" or "85,50". */
        fun formatAmountInput(minor: Long): String =
            if (minor % 100 == 0L) (minor / 100).toString() else "${minor / 100},${(minor % 100).toString().padStart(2, '0')}"
    }
}
